import { useState, useEffect, useCallback } from 'react';
import { Plant, Cell, BedPlan } from './types';
import { fetchPlants, saveGarden, deleteGarden } from './services/api';
import BedConfigurator from './components/BedConfigurator';
import GardenGrid from './components/GardenGrid';
import PlantSidebar from './components/PlantSidebar';
import BedHistory from './components/BedHistory';
import './styles/variables.css';
import './App.css';

type View = 'configure' | 'plan';

type AppProps = {
  signOut?: () => void;
  user?: { signInDetails?: { loginId?: string }; username?: string };
};

function generateId() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export default function App({ signOut, user }: AppProps = {}) {
  const userLabel = (user?.signInDetails?.loginId ?? '').split('@')[0];
  const [view, setView] = useState<View>('configure');
  const [plants, setPlants] = useState<Plant[]>([]);
  const [widthFt, setWidthFt] = useState(4);
  const [heightFt, setHeightFt] = useState(4);
  const [cells, setCells] = useState<Cell[]>([]);
  const [previousCells, setPreviousCells] = useState<Cell[]>([]);
  const [currentPlanId, setCurrentPlanId] = useState<string>(generateId());
  const [bedName, setBedName] = useState('');
  const [season, setSeason] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetchPlants().then(setPlants);
    const now = new Date();
    const month = now.getMonth();
    const year = now.getFullYear();
    const seasonName = month < 3 ? 'Winter' : month < 6 ? 'Spring' : month < 9 ? 'Summer' : 'Autumn';
    setSeason(`${seasonName} ${year}`);
  }, []);

  const handleCreateBed = (w: number, h: number, name: string) => {
    setWidthFt(w);
    setHeightFt(h);
    setBedName(name);
    setCells([]);
    setPreviousCells([]);
    setCurrentPlanId(generateId());
    setSaved(false);
    setView('plan');
  };

  const handleSave = useCallback(async () => {
    setSaving(true);
    const plan: BedPlan = {
      gardenId: currentPlanId,
      userId: 'local-user',
      name: bedName,
      season,
      widthFt,
      heightFt,
      cells,
      previousCells,
      savedAt: new Date().toISOString(),
    };
    await saveGarden(plan);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }, [currentPlanId, bedName, season, widthFt, heightFt, cells, previousCells]);

  const handleLoadPlan = (plan: BedPlan) => {
    setWidthFt(plan.widthFt);
    setHeightFt(plan.heightFt);
    setCells(plan.cells);
    setPreviousCells(plan.previousCells ?? []);
    setCurrentPlanId(plan.gardenId);
    setBedName(plan.name);
    setSeason(plan.season);
    setSaved(false);
    setView('plan');
  };

  const handleDeletePlan = useCallback(async (gardenId: string) => {
    await deleteGarden(gardenId);
    if (gardenId === currentPlanId) {
      setView('configure');
    }
  }, [currentPlanId]);

  const handleNewSeason = () => {
    // current cells become the rotation reference for the new season
    setPreviousCells(cells);
    setCells([]);
    setCurrentPlanId(generateId());
    const now = new Date();
    const year = now.getFullYear() + 1;
    setSeason(`Spring ${year}`);
    setSaved(false);
  };

  if (view === 'configure') {
    return (
      <div className="home">
        <div className="app-authbar">
          {userLabel && <span className="app-authbar__user">👤 {userLabel}</span>}
          <button className="app-btn app-btn--outline" onClick={signOut}>Sign out</button>
        </div>
        <BedConfigurator onCreateBed={handleCreateBed} />
        <div className="home__history">
          <BedHistory onLoad={handleLoadPlan} onDelete={handleDeletePlan} currentPlanId={null} title="🌿 Your saved beds" />
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="app-header__inner">
          <button className="app-btn app-btn--outline app-header__newbed" onClick={() => setView('configure')} aria-label="Back to home">
            ← Home
          </button>
          <div className="app-header__title">
            <h1 className="app-header__name">
              <span className="app-header__name-text">{bedName}</span>
            </h1>
            <span className="app-header__season-wrap">
              <span className="app-header__sep" aria-hidden="true">·</span>
              <input
                className="app-season app-season--input"
                value={season}
                onChange={e => setSeason(e.target.value)}
                size={Math.max(season.length, 6)}
                aria-label="Season name"
              />
            </span>
          </div>
          <div className="app-header__actions">
            <button
              className={`app-btn${saved ? ' app-btn--saved' : ''}`}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving…' : saved ? '✓ Saved!' : 'Save plan'}
            </button>
            {cells.some(c => c.plantId) && (
              <button className="app-btn app-btn--outline" onClick={handleNewSeason}>
                Plan next season
              </button>
            )}
            <button className="app-btn app-btn--outline" onClick={signOut}>Sign out</button>
          </div>
        </div>
      </header>

      <main className="app-main">
        <div className="app-layout">
          <div className="app-layout__grid">
            <div className="app-bed-info">
              <span className="app-bed-info__size">{widthFt} × {heightFt} ft bed</span>
              <span className="app-bed-info__count">
                {cells.filter(c => c.plantId).length} of {widthFt * heightFt} squares planted
              </span>
            </div>
            <GardenGrid
              widthFt={widthFt}
              heightFt={heightFt}
              plants={plants}
              cells={cells}
              previousCells={previousCells}
              onCellsChange={setCells}
            />
            <BedHistory
              onLoad={handleLoadPlan}
              onDelete={handleDeletePlan}
              currentPlanId={currentPlanId}
              filterName={bedName}
              title={`📚 Past seasons of ${bedName}`}
            />
          </div>
          <div className="app-layout__sidebar">
            <PlantSidebar plants={plants} />
          </div>
        </div>
      </main>
    </div>
  );
}
